import React, { useEffect, useState } from 'react';
import { PauseIcon, PlayIcon } from 'lucide-react';
import { stills } from '../data/projects';
import type { VideoFormat } from '../types/portal';
import { EMBED_ERROR, toDrivePreviewUrl } from '../utils/drive';
interface VideoStageProps {
  driveUrl: string;
  label: string;
  runtime: string;
  /** Framing set by Portal. Absent means landscape, so nothing existing breaks. */
  format?: VideoFormat;
}
function toClock(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const rest = Math.floor(seconds % 60);
  return `${String(minutes).padStart(2, '0')}:${String(rest).padStart(2, '0')}`;
}
export function VideoStage({
  driveUrl,
  label,
  runtime,
  format
}: VideoStageProps) {
  const [playing, setPlaying] = useState(false);
  const [position, setPosition] = useState(0);
  const total = 1724;
  /* The wrapper alone sets the ratio — Drive's own content is never stretched. */
  const vertical = format === '9:16 Vertical';
  const embedUrl = toDrivePreviewUrl(driveUrl);
  const linkProvided = Boolean(driveUrl.trim());
  /** Only the link itself can be judged — Drive is cross-origin and opaque. */
  const showError = linkProvided && !embedUrl;
  useEffect(() => {
    setPlaying(false);
    setPosition(0);
  }, [driveUrl]);
  useEffect(() => {
    if (!playing || embedUrl) return;
    const timer = window.setInterval(() => {
      setPosition(current => current >= total ? 0 : current + 1);
    }, 120);
    return () => window.clearInterval(timer);
  }, [playing, embedUrl, total]);
  return <figure className={vertical ? 'm-0 mx-auto w-full max-w-[440px]' : 'm-0'} data-id="element-413" data-mp="24xd3bs1lelue">
      <div className={['relative w-full overflow-hidden bg-ink', vertical ? 'aspect-[9/16]' : 'aspect-video'].join(' ')} data-id="element-414" data-mp="2io8fpebavl2q">
        {embedUrl ? <iframe title={label} src={embedUrl} allow="autoplay; fullscreen" allowFullScreen className="h-full w-full border-0" data-id="element-415" data-mp="1q5ui9ftvm146" /> : showError ? <div className="flex h-full w-full flex-col items-start justify-end p-6 sm:p-8" data-id="element-416" data-mp="3l9j0ztjjeuat">
            <img src={stills.studio} alt="" className="absolute inset-0 h-full w-full object-cover opacity-20 grayscale" data-id="element-417" data-mp="3q7kl8hsp8znv" />
            <div className="absolute inset-0 bg-ink/70" data-id="element-418" data-mp="3sj2t2atm1yx" />
            <div className="relative" data-id="element-419" data-mp="3vf4n1pshasn">
              <p className="label m-0 text-paper/50" data-id="element-420" data-mp="32c9j4gtv1ovo">Preview Unavailable</p>
              <p className="mt-4 max-w-md font-display text-xl leading-snug text-paper sm:text-2xl" data-id="element-421" data-mp="37a17x1z7kuvn">
                {EMBED_ERROR}
              </p>
              <a href={driveUrl} target="_blank" rel="noreferrer" className="label mt-7 inline-block border border-paper/50 px-5 py-3 text-paper transition-colors duration-150 ease-out hover:bg-paper hover:text-ink" data-id="element-422" data-mp="3c7t3c5z2az4l">
                Open in Google Drive
              </a>
            </div>
          </div> : <>
            <img src={stills.studio} alt="" className="absolute inset-0 h-full w-full object-cover opacity-40 grayscale" data-id="element-423" data-mp="2h2da8t7rrw9h" />
            <div className="absolute inset-0 bg-ink/40" data-id="element-424" data-mp="27964v8d14g9e" />
            <div className="absolute inset-0 flex flex-col justify-between p-6 sm:p-8" data-id="element-425" data-mp="22bntlbbjhoy8">
              <p className="label text-paper/60" data-id="element-426" data-mp="1xcbb12rpb41g">Google Drive Video Preview</p>
              <div className="flex items-end justify-between gap-6" data-id="element-427" data-mp="1xa6wzs038ong">
                <div data-id="element-428" data-mp="1t9woo8otpm5f">
                  <p className="label text-paper/50" data-id="element-429" data-mp="1tcspc4s22oxk">{playing ? 'Playing' : 'Paused'}</p>
                  <p className="mt-2 font-sans text-xs tracking-wide2 text-paper/80" data-id="element-430" data-mp="1obvaa12ceb0a">
                    {toClock(position)} / {runtime}
                  </p>
                </div>
                <button type="button" onClick={() => setPlaying(current => !current)} aria-label={playing ? 'Pause preview' : 'Play preview'} className="flex h-16 w-16 items-center justify-center rounded-full border border-paper/40 text-paper transition-colors duration-150 ease-out hover:border-paper hover:bg-paper hover:text-ink sm:h-20 sm:w-20" data-id="element-431" data-mp="3iobdsu3r7b1g">
                  {playing ? <PauseIcon className="h-5 w-5" strokeWidth={1} data-id="element-432" data-mp="26d943aaq5l3o" /> : <PlayIcon className="h-5 w-5 translate-x-[1px]" strokeWidth={1} data-id="element-433" data-mp="1romca93wv0vk" />}
                </button>
              </div>
            </div>
            <div className="absolute bottom-0 left-0 h-px w-full bg-paper/20" data-id="element-434" data-mp="1whpa4tfq0whk">
              <div className="h-px bg-paper transition-[width] duration-150 ease-linear" style={{
            width: `${position / total * 100}%`
          }} data-id="element-435" data-mp="1iqaqckdzor9g" />
            </div>
          </>}
      </div>

      <figcaption className="mt-3 flex flex-wrap items-center justify-between gap-3" data-id="element-436" data-mp="1dpd6m3dbzp5u">
        <span className="label text-mute" data-id="element-437" data-mp="1dr7jqvid2czd">{label}</span>
        {embedUrl ? <a href={driveUrl} target="_blank" rel="noreferrer" className="label text-mute transition-colors duration-150 ease-out hover:text-ink" data-id="element-438" data-mp="u3uuebbm9p0c">
            Open in Google Drive
          </a> : <span className="label text-mute" data-id="element-439" data-mp="z1mn37k35f5l">
            {showError ? 'Check the Drive link' : 'Awaiting Drive preview link'}
          </span>}
      </figcaption>
    </figure>;
}